// Crash telemetry. The gameplay analytics in analytics.ts answer "where do
// players get stuck"; this answers "where does the game break," which until
// now nothing did -- there was no error boundary anywhere in game/, so an
// exception in a cast handler, applySimEvents or performCast propagated into
// Phaser's loop or a DOM handler, killed whatever was running, and left the
// developer with no signal at all. A build is already with playtesters, so
// every crash they hit is currently invisible.
//
// Deliberately built on top of trackEvent rather than beside it: same
// anonymous playerId/runId, same batching, same devMode gating, same
// server-side eventId dedup. A crash is an analytics event that happens to be
// about the code instead of about the player.
//
// The one thing this module does that a naive `window.onerror -> POST` would
// not: it survives a throw inside a per-frame loop. GameScene.update() runs
// ~60x/second, so a single bad frame is not one report, it is 60 reports a
// second until the tab closes -- which would flood the client queue, flood
// D1, and bury the one row that mattered. Hence the signature dedup below.
import { trackEvent, flushAnalyticsNow } from './analytics';

// Enough to tell one bug from another; short enough that a repeated crash
// costs one row, not thousands.
const MAX_MESSAGE_LEN = 300;
const MAX_STACK_LEN = 1200;
// analyticsApi.ts caps a stored payload at 4000 chars and, over the cap,
// stores a marker rather than the payload (truncating JSON at a byte count
// stores broken JSON, not smaller JSON -- see its own comment). The two limits
// above keep a report comfortably under that, where the structure is still
// known and the truncation can be made at a sensible place.

// A session producing more than this many *distinct* crashes is already fully
// diagnosed by the ones it did send.
const MAX_DISTINCT_SIGNATURES = 10;

// Where the error was caught, not where it was thrown -- the stack says where
// it was thrown. This is the boundary that held, which is what tells you
// whether the game kept running afterward and in what state.
export type ErrorContext = 'window' | 'unhandled-rejection' | 'boot' | 'cast' | 'sim-events' | 'update';

interface SignatureRecord {
  count: number;
  reported: boolean;
}

const seen = new Map<string, SignatureRecord>();
let droppedDistinct = 0;

// Set by GameScene once it exists. Kept as a pluggable provider rather than an
// import of activeWorld so this module stays dependency-free and installable
// before the scene (or the world) exists at all -- a crash during boot is
// exactly the crash you most want reported, and it happens before there is a
// room to name.
type ContextProvider = () => Record<string, unknown>;
let contextProvider: ContextProvider | null = null;

export function setErrorContextProvider(provider: ContextProvider): void {
  contextProvider = provider;
}

function describe(error: unknown): { name: string; message: string; stack: string } {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: (error.message || '').slice(0, MAX_MESSAGE_LEN),
      stack: (error.stack || '').slice(0, MAX_STACK_LEN),
    };
  }
  // Non-Error throws are legal in JS and do happen (a rejected promise
  // carrying a string, a DOM event object). Stringify defensively rather than
  // dropping the report -- a bad report beats no report.
  let text: string;
  try {
    text = typeof error === 'string' ? error : JSON.stringify(error);
  } catch {
    text = String(error);
  }
  return { name: 'NonError', message: (text ?? 'unknown').slice(0, MAX_MESSAGE_LEN), stack: '' };
}

// Two errors are "the same bug" if they have the same name, the same message
// and the same innermost stack frame. Message alone is too coarse (the same
// generic TypeError text from two unrelated call sites reads as one bug); the
// full stack is too fine (a stack that differs only in a frame's column number
// across two renders reads as two bugs).
function signatureOf(name: string, message: string, stack: string): string {
  const firstFrame = stack.split('\n').find((line) => /^\s*at\s/.test(line))?.trim() ?? '';
  return `${name}|${message}|${firstFrame}`;
}

// The single entry point. Never throws -- a failure inside the crash reporter
// while reporting a crash is how a recoverable bug becomes an infinite loop.
export function reportError(error: unknown, context: ErrorContext, extra: Record<string, unknown> = {}): void {
  try {
    const { name, message, stack } = describe(error);
    const signature = signatureOf(name, message, stack);

    let record = seen.get(signature);
    if (!record) {
      if (seen.size >= MAX_DISTINCT_SIGNATURES) {
        // Past the cap the rest are noise, but how much noise is itself worth
        // knowing, so the dropped count ships with the summary on unload.
        droppedDistinct += 1;
        return;
      }
      record = { count: 0, reported: false };
      seen.set(signature, record);
    }
    record.count += 1;
    if (record.reported) return;
    record.reported = true;

    trackEvent('client_error', {
      context,
      name,
      message,
      stack,
      ...safeContext(),
      ...extra,
    });
    // A crash is worth the round trip immediately: the tab may be about to
    // die, and unlike a room_enter there is no later batch to ride along with.
    // The 10s flush timer is for volume, not for this.
    flushAnalyticsNow();
  } catch {
    // Swallowed on purpose. See the comment above.
  }
}

function safeContext(): Record<string, unknown> {
  try {
    return contextProvider?.() ?? {};
  } catch {
    return {};
  }
}

// Repeats are counted but not sent, so without this a crash that fired once
// and a crash that fired forty thousand times look identical in the table --
// and those are very different bugs. One summary row per repeated signature,
// emitted when the page goes away.
function reportRepeats(): void {
  try {
    for (const [signature, record] of seen) {
      if (record.count <= 1) continue;
      const [name, message] = signature.split('|');
      trackEvent('client_error_summary', { name, message, occurrences: record.count });
    }
    if (droppedDistinct > 0) trackEvent('client_error_summary', { name: 'DroppedDistinct', message: 'distinct signatures over cap', occurrences: droppedDistinct });
    // Cleared so a page that becomes visible again and then hides again does
    // not re-send the same totals.
    seen.clear();
    droppedDistinct = 0;
  } catch {
    // As above.
  }
}

// Installed from main.ts as early as the module graph allows -- before
// awaitPlaytest, before initUI, before Phaser exists. Anything thrown during
// boot lands here rather than in a silent console nobody is reading.
export function initErrorTelemetry(): void {
  window.addEventListener('error', (event) => {
    // ErrorEvent.error is the thrown value; it is null for cross-origin
    // script errors, where message/filename is all the browser will give us.
    reportError(event.error ?? `${event.message} (${event.filename}:${event.lineno})`, 'window');
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, 'unhandled-rejection');
  });
  // Same two hooks analytics.ts already flushes on, for the same reason: this
  // is the last moment the summary can still be sent. The ordering matters and
  // is not accidental -- initErrorTelemetry runs from main.ts at module load,
  // analytics.ts's own listeners are registered by initAnalytics from
  // GameScene.create(), so these fire first and the summary is queued before
  // the flush that carries it. Moving this call later would silently drop
  // every summary.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') reportRepeats();
  });
  window.addEventListener('pagehide', () => reportRepeats());
}

// A crash the player can see beats a crash that silently does nothing -- a
// frozen HUD with no explanation reads as "the game is broken" and gets
// reported as a vague complaint, where a named failure gets reported as a bug.
// Deliberately not a modal: the game underneath is usually still playable, and
// stealing focus from a player mid-puzzle to announce a recovered error is a
// worse outcome than the error.
export function showErrorNotice(text: string): void {
  try {
    const el = document.getElementById('cast-result-hud');
    if (el) el.innerHTML = `<p class="muted small">${text}</p>`;
  } catch {
    // As above.
  }
}
